import { StandardEvents } from '@shopify/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * SD Cart Recommendations (WEB-338)
 *
 * In-drawer "You May Also Like" with smart sourcing:
 *   mode="smart" (default): walk the cart's products high→low price and use the first
 *     that has curated COMPLEMENTARY products (set up in Search & Discovery); if none
 *     do, fall back to RELATED for the highest-priced product.
 *   mode="related": always RELATED for the highest-priced product.
 *
 * The Product Recommendations API returns an empty section for intent=complementary
 * when a product has none set up — that empty result (data-recs-count="0") is exactly
 * what drives the fallback.
 *
 * Loads eagerly on connect (page load) and loads once: the drawer must open with
 * the recs already present — an in-view lazy-load inside the closed dialog meant
 * the shopper watched them pop in (WEB-338 QA feedback). True SSR is not possible:
 * Shopify only populates `recommendations` through its client-fetched endpoint.
 * The mount is morph-preserved (data-skip-*-update), so cart changes don't
 * re-fetch. Injected markup (heading + resource-list-carousel) upgrades on
 * insertion, same as the native recs path.
 *
 * ponytail: sequential fetch with early-exit — carts are small and the common case
 * (priciest product has complementary) is one request. Parallelize if it ever matters.
 */
class SdCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-ids'];

  #loaded = false;

  /** Monotonic load token: a newer load supersedes an in-flight older one. */
  #seq = 0;

  /** @type {Map<string, string | null>} url → inner markup (or null when no recs) */
  #cache = new Map();

  /** @type {MutationObserver | null} mirrors slide aria-hidden onto inert */
  #inertObserver = null;

  connectedCallback() {
    this.#load();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  /**
   * An add made while the drawer is open (a card's Add to Cart, or the quick view a card
   * opens) morphs the new line in at the top of the list, but the list keeps its scroll
   * position, so a shopper who had scrolled down to these cards saw the new line cut off
   * under the header (WEB-460). Bring the list back to the top once the morph has landed.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = async (event) => {
    const dialog = this.closest('dialog');
    if (event.action !== 'add' || !dialog?.open) return;

    try {
      const resolved = await event.promise;
      if (resolved?.detail?.didError) return;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[sd-cart-recs] Event promise rejected:', error);
      return;
    }

    // product-form adds carry the drawer's section HTML, so cart-items-component morphs on
    // this same promise and a frame later the new line is in. An add without it re-renders
    // later, into a list that is already at the top.
    requestAnimationFrame(() => {
      dialog
        .querySelector('.cart-drawer__items')
        ?.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    });
  };

  /**
   * Cart transitions (empty ↔ filled, composition changes) morph new seed ids onto
   * the mount — its children are morph-preserved (data-skip-subtree-update), so this
   * is the signal to re-source. The initial set (oldValue null) is connect's job.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue) return;
    this.#loaded = false;
    this.#load();
  }

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;
    const seq = ++this.#seq;

    const ids = (this.dataset.productIds || '').split(',').filter(Boolean);
    const mode = this.dataset.mode || 'smart';
    if (!ids.length) return this.#hide();

    let markup = null;

    // Curated complementary wins — check products high→low price, first hit stops.
    // Each fetch guards itself, so one candidate failing just falls through to the next.
    if (mode === 'smart') {
      for (const id of ids) {
        markup = await this.#fetch(id, 'complementary');
        if (markup) break;
      }
    }

    // Related fallback (and the 'related' mode's only source): highest-priced product.
    // Always reachable even if the complementary probes above errored out.
    if (!markup) {
      markup = await this.#fetch(ids[0], 'related');
    }

    // A newer load (fresh seed ids) started while this one was in flight — drop it.
    if (seq !== this.#seq) return;

    if (markup) {
      this.classList.remove('hidden');
      this.innerHTML = markup;
      this.#syncInertSlides();
    } else {
      this.#hide();
    }
  }

  /**
   * Fetch the rendered recommendations section for one product + intent. Never throws:
   * a transient failure returns null (uncached, so it isn't remembered as "no recs")
   * and lets the caller try the next candidate / the related fallback.
   * @param {string} productId
   * @param {'related' | 'complementary'} intent
   * @returns {Promise<string | null>} the result inner markup when it has products, else null
   */
  async #fetch(productId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}&intent=${intent}`;

    if (this.#cache.has(url)) return this.#cache.get(url);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const html = document.createElement('div');
      html.innerHTML = await response.text();

      const result = html.querySelector('#sd-cart-recommendations-result');
      const count = Number(result?.dataset.recsCount || 0);
      const markup = result && count > 0 ? result.innerHTML : null;

      this.#cache.set(url, markup); // cache the definitive result (including a legitimate empty)
      return markup;
    } catch (error) {
      if (!window.Shopify?.designMode) {
        console.error('SD cart recommendations:', error.message);
      }
      return null;
    }
  }

  #hide() {
    this.classList.add('hidden');
  }

  /**
   * Keeps `inert` in step with the carousel's own `aria-hidden` on off-screen slides.
   *
   * The slideshow marks hidden slides `aria-hidden` only, which removes them from the
   * accessibility tree but leaves them keyboard focusable. That is harmless for a slide
   * of plain links, but this carousel now renders variant swatches, so without `inert`
   * the three off-screen cards become invisible tab stops — the exact pattern the repo
   * CLAUDE.md forbids.
   *
   * Scoped here rather than in the shared slideshow: `inert` also blocks pointer events,
   * which would break carousels whose slides use click-to-navigate. Mirroring the
   * theme's own visibility flag keeps this in sync without duplicating that logic.
   */
  #syncInertSlides() {
    const slides = this.querySelectorAll('slideshow-slide');
    if (!slides.length) return;

    const apply = (/** @type {Element} */ slide) => {
      if (slide.getAttribute('aria-hidden') === 'true') slide.setAttribute('inert', '');
      else slide.removeAttribute('inert');
    };

    for (const slide of slides) apply(slide);

    this.#inertObserver?.disconnect();
    this.#inertObserver = new MutationObserver((records) => {
      for (const record of records) apply(/** @type {Element} */ (record.target));
    });

    for (const slide of slides) {
      this.#inertObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#inertObserver?.disconnect();
    this.#inertObserver = null;
  }
}

if (!customElements.get('sd-cart-recommendations')) {
  customElements.define('sd-cart-recommendations', SdCartRecommendations);
}
